import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  ArrowLeft,
  ChevronDown,
  ChevronRight,
  Package,
  Search,
} from "lucide-react";

import {
  useNavigate,
  useSearchParams,
} from "react-router-dom";

import api from "../lib/Urls";
import { getStoredUserId } from "../lib/authStorage";
import {
  CHAT_DELIVERED_DESTINATION,
} from "../lib/chatRealtime";

import Navbar from "../components/Navbar";
import ChatWindow from "../components/ChatWindow";
import ProfileImageViewer from "../components/ProfileImageViewer";
import { useChatSocket } from "../contexts/ChatSocketContext.jsx";

/* ========================================================= */
/* HELPERS */
/* ========================================================= */

const getOtherUserId = (chat, userId) =>
  Number(chat.senderId) === Number(userId)
    ? Number(chat.receiverId)
    : Number(chat.senderId);

const getChatTimestamp = (value) => {
  const raw = Number(value);

  if (Number.isFinite(raw)) {
    return raw < 1_000_000_000_000
      ? raw * 1000
      : raw;
  }

  const parsed = new Date(value).getTime();

  return Number.isFinite(parsed)
    ? parsed
    : 0;
};

const toChatSelection = (
  chat,
  userId
) => {
  if (!chat?.roomId || !chat?.itemId) {
    return null;
  }

  return {
    roomId: chat.roomId,

    itemId:
      Number(chat.itemId),

    otherUserId:
      chat.otherUserId
        ? Number(chat.otherUserId)
        : getOtherUserId(
            chat,
            userId
          ),

    otherUsername:
      chat.otherUsername ||
      null,

    otherProfileImageUrl:
      chat.otherProfileImageUrl ||
      null,

    itemName:
      chat.itemName ||
      null,

    itemImageUrl:
      chat.itemImageUrl ||
      null,
  };
};

const getOtherUserFromChat = (
  chat,
  userId
) => ({
  userId:
    chat?.otherUserId
      ? Number(chat.otherUserId)
      : getOtherUserId(
          chat,
          userId
        ),

  username:
    chat?.otherUsername ||
    "Marketplace user",

  profileImageUrl:
    chat?.otherProfileImageUrl ||
    null,
});

const getPersonKey = (
  chat,
  userId
) =>
  String(
    chat?.otherUserId
      ? Number(chat.otherUserId)
      : getOtherUserId(
          chat,
          userId
        )
  );

const readStoredSelection = (
  key,
  userId
) => {
  try {
    const saved =
      sessionStorage.getItem(key);

    if (!saved) {
      return null;
    }

    return toChatSelection(
      JSON.parse(saved),
      userId
    );
  } catch {
    sessionStorage.removeItem(key);

    return null;
  }
};

const readJsonObject = (
  key
) => {
  try {
    const parsed =
      JSON.parse(
        sessionStorage.getItem(key) ||
          "{}"
      );

    return (
      parsed &&
      typeof parsed === "object" &&
      !Array.isArray(parsed)
    )
      ? parsed
      : {};
  } catch {
    sessionStorage.removeItem(key);

    return {};
  }
};

/* ========================================================= */
/* INBOX PAGE */
/* ========================================================= */

const InboxPage = () => {
  const navigate =
    useNavigate();

  const {
    subscribe,
    publish,
  } = useChatSocket();

  const userId = getStoredUserId();

  const [
    searchParams,
    setSearchParams,
  ] =
    useSearchParams();

  const requestedRoomId =
    searchParams.get(
      "roomId"
    );

  const inboxCacheKey =
    `inbox_cache_${userId}`;

  const activeChatKey =
    `activeChat_${userId}`;

  const readStateKey =
    `chat_read_state_${userId}`;

  /* ======================================================= */
  /* STATE */
  /* ======================================================= */

  const [
    chats,
    setChats,
  ] = useState(() => {
    try {
      const cached =
        sessionStorage.getItem(
          inboxCacheKey
        );

      return cached
        ? JSON.parse(cached)
        : [];
    } catch {
      sessionStorage.removeItem(
        inboxCacheKey
      );

      return [];
    }
  });

  const [
    selectedChat,
    setSelectedChat,
  ] = useState(() =>
    readStoredSelection(
      activeChatKey,
      userId
    )
  );

  const [
    searchQuery,
    setSearchQuery,
  ] = useState("");

  const [
    readState,
    setReadState,
  ] = useState(() =>
    readJsonObject(
      readStateKey
    )
  );

  const [
    expandedPeople,
    setExpandedPeople,
  ] = useState(
    () => new Set()
  );

  /* ======================================================= */
  /* REFS */
  /* ======================================================= */

  const selectedChatRef =
    useRef(
      selectedChat
    );

  const handledNotificationRoomRef =
    useRef(null);

  useEffect(() => {
    selectedChatRef.current =
      selectedChat;
  }, [selectedChat]);

  /* ======================================================= */
  /* CACHE */
  /* ======================================================= */

  const persistChats =
    useCallback(
      (nextChats) => {
        sessionStorage.setItem(
          inboxCacheKey,
          JSON.stringify(
            nextChats
          )
        );
      },
      [inboxCacheKey]
    );

  /* ======================================================= */
  /* MARK ROOM READ */
  /* ======================================================= */

  const markRoomRead =
    useCallback(
      (
        roomId,
        activityTime =
          Date.now(),
        persistServer = true
      ) => {
        if (!roomId) {
          return;
        }

        const normalized =
          getChatTimestamp(
            activityTime
          ) ||
          Date.now();

        setReadState(
          (current) => {
            const previous =
              Number(
                current[
                  roomId
                ] || 0
              );

            if (
              previous >=
              normalized
            ) {
              return current;
            }

            const next = {
              ...current,

              [roomId]:
                normalized,
            };

            sessionStorage.setItem(
              readStateKey,
              JSON.stringify(
                next
              )
            );

            return next;
          }
        );

        setChats(
          (current) => {
            let changed =
              false;

            const next =
              current.map(
                (chat) => {
                  if (
                    chat.roomId !==
                      roomId ||
                    !chat.unreadCount
                  ) {
                    return chat;
                  }

                  changed =
                    true;

                  return {
                    ...chat,

                    unreadCount: 0,
                  };
                }
              );

            if (changed) {
              persistChats(
                next
              );
            }

            return changed
              ? next
              : current;
          }
        );

        if (
          persistServer
        ) {
          api
            .put(
              `/api/chat/rooms/${encodeURIComponent(
                roomId
              )}/read`,
              {
                readAt:
                  normalized,
              }
            )
            .catch(
              (error) => {
                console.error(
                  "Failed to persist inbox read state",
                  error
                );
              }
            );
        }
      },
      [
        persistChats,
        readStateKey,
      ]
    );

  /* ======================================================= */
  /* UNREAD HELPERS */
  /* ======================================================= */

  const isUnread =
    useCallback(
      (chat) => {
        if (
          !chat?.roomId ||
          Number(
            chat.senderId
          ) === userId
        ) {
          return false;
        }

        if (
          Number(
            chat.unreadCount
          ) > 0
        ) {
          return true;
        }

        const lastActivity =
          getChatTimestamp(
            chat.timestamp
          );

        const lastRead =
          Number(
            readState[
              chat.roomId
            ] || 0
          );

        return (
          lastActivity >
          lastRead
        );
      },
      [
        readState,
        userId,
      ]
    );

  const unreadForChat =
    useCallback(
      (chat) => {
        if (
          !isUnread(chat)
        ) {
          return 0;
        }

        const serverCount =
          Number(
            chat.unreadCount
          );

        return (
          Number.isFinite(
            serverCount
          ) &&
          serverCount > 0
        )
          ? serverCount
          : 1;
      },
      [isUnread]
    );

  /* ======================================================= */
  /* LOAD INBOX */
  /* ======================================================= */

  useEffect(() => {
    api
      .get(
        "/api/chat/inbox"
      )
      .then(
        (res) => {
          const nextChats =
            Array.isArray(
              res.data
            )
              ? res.data
              : [];

          setChats(
            nextChats
          );

          persistChats(
            nextChats
          );
        }
      )
      .catch(
        (error) => {
          console.error(
            "Failed to load inbox",
            error
          );
        }
      );
  }, [
    persistChats,
    userId,
  ]);

  /* ======================================================= */
  /* REALTIME INBOX - SHARED APP SOCKET */
  /* ======================================================= */

  useEffect(() => {
    if (!userId) {
      return undefined;
    }

    return subscribe(
      `/topic/inbox/${userId}`,
      (msg) => {
        try {
          const newChat = JSON.parse(msg.body);
          const activeRoomId = selectedChatRef.current?.roomId;
          const incomingFromOtherUser =
            Number(newChat?.senderId) !== userId;
          const messageId =
            newChat?.messageId ??
            newChat?.id ??
            null;

          if (
            incomingFromOtherUser &&
            messageId !== null &&
            activeRoomId !== newChat.roomId
          ) {
            publish(
              CHAT_DELIVERED_DESTINATION,
              {
                roomId: newChat.roomId,
                messageId,
                deliveredAt: Date.now(),
              },
              {
                queueIfDisconnected: false,
              }
            );
          }

          setChats((prev) => {
            const previousChat = prev.find(
              (chat) => chat.roomId === newChat.roomId
            );

            const filtered = prev.filter(
              (chat) => chat.roomId !== newChat.roomId
            );

            const nextUnreadCount =
              activeRoomId === newChat.roomId || !incomingFromOtherUser
                ? 0
                : Number(newChat.unreadCount) > 0
                ? Number(newChat.unreadCount)
                : Number(previousChat?.unreadCount || 0) + 1;

            const updatedChat = {
              ...previousChat,
              ...newChat,
              otherUsername:
                newChat.otherUsername ||
                previousChat?.otherUsername ||
                null,
              otherProfileImageUrl:
                newChat.otherProfileImageUrl ||
                previousChat?.otherProfileImageUrl ||
                null,
              itemName:
                newChat.itemName ||
                previousChat?.itemName ||
                null,
              itemImageUrl:
                newChat.itemImageUrl ||
                previousChat?.itemImageUrl ||
                null,
              unreadCount: nextUnreadCount,
            };

            const updated = [updatedChat, ...filtered];
            persistChats(updated);
            return updated;
          });

          if (activeRoomId === newChat.roomId && incomingFromOtherUser) {
            markRoomRead(
              newChat.roomId,
              newChat.timestamp || Date.now(),
              false
            );
          }
        } catch (error) {
          console.error(
            "Invalid inbox update payload",
            error
          );
        }
      }
    );
  }, [
    markRoomRead,
    persistChats,
    publish,
    subscribe,
    userId,
  ]);

  /* ======================================================= */
  /* SYNC SELECTED CHAT METADATA */
  /* ======================================================= */

  useEffect(() => {
    if (
      !selectedChat?.roomId ||
      !chats.length
    ) {
      return;
    }

    const latest =
      chats.find(
        (chat) =>
          chat.roomId ===
          selectedChat.roomId
      );

    if (!latest) {
      return;
    }

    const refreshed =
      toChatSelection(
        latest,
        userId
      );

    if (!refreshed) {
      return;
    }

    const changed =
      refreshed.otherUsername !==
        selectedChat.otherUsername ||
      refreshed.otherProfileImageUrl !==
        selectedChat.otherProfileImageUrl ||
      refreshed.otherUserId !==
        selectedChat.otherUserId;

    if (!changed) {
      return;
    }

    setSelectedChat(
      refreshed
    );

    sessionStorage.setItem(
      activeChatKey,
      JSON.stringify(
        refreshed
      )
    );
  }, [
    activeChatKey,
    chats,
    selectedChat,
    userId,
  ]);

  /* ======================================================= */
  /* NOTIFICATION DEEP LINK */
  /* ======================================================= */

  useEffect(() => {
    if (
      !requestedRoomId ||
      !chats.length ||
      handledNotificationRoomRef
        .current ===
        requestedRoomId
    ) {
      return;
    }

    const chat =
      chats.find(
        (candidate) =>
          candidate.roomId ===
          requestedRoomId
      );

    if (!chat) {
      return;
    }

    const selection =
      toChatSelection(
        chat,
        userId
      );

    if (!selection) {
      return;
    }

    handledNotificationRoomRef.current =
      requestedRoomId;

    setSelectedChat(
      selection
    );

    sessionStorage.setItem(
      activeChatKey,
      JSON.stringify(
        selection
      )
    );

    setExpandedPeople(
      (current) => {
        const next =
          new Set(
            current
          );

        next.add(
          getPersonKey(
            chat,
            userId
          )
        );

        return next;
      }
    );

    markRoomRead(
      chat.roomId,
      chat.timestamp ||
        Date.now(),
      false
    );

    const nextParams =
      new URLSearchParams(
        searchParams
      );

    nextParams.delete(
      "roomId"
    );

    setSearchParams(
      nextParams,
      {
        replace: true,
      }
    );
  }, [
    activeChatKey,
    chats,
    markRoomRead,
    requestedRoomId,
    searchParams,
    setSearchParams,
    userId,
  ]);

  /* ======================================================= */
  /* SELECT CHAT */
  /* ======================================================= */

  const handleSelectChat =
    (chat) => {
      const selection =
        toChatSelection(
          chat,
          userId
        );

      if (!selection) {
        return;
      }

      setSelectedChat(
        selection
      );

      sessionStorage.setItem(
        activeChatKey,
        JSON.stringify(
          selection
        )
      );

      setExpandedPeople(
        (current) => {
          const next =
            new Set(
              current
            );

          next.add(
            getPersonKey(
              chat,
              userId
            )
          );

          return next;
        }
      );

      markRoomRead(
        chat.roomId,
        chat.timestamp ||
          Date.now(),
        false
      );
    };

  /* ======================================================= */
  /* MOBILE CHAT -> INBOX */
  /* ======================================================= */

  const handleBackToInbox =
    () => {
      setSelectedChat(
        null
      );

      sessionStorage.removeItem(
        activeChatKey
      );
    };

  /* ======================================================= */
  /* SORTED CHATS */
  /* ======================================================= */

  const sortedChats =
    useMemo(
      () =>
        [...chats].sort(
          (a, b) =>
            getChatTimestamp(
              b.timestamp
            ) -
            getChatTimestamp(
              a.timestamp
            )
        ),
      [chats]
    );

  /* ======================================================= */
  /* GROUP BY PERSON */
  /* ======================================================= */

  const conversationGroups =
    useMemo(() => {
      const groups =
        new Map();

      sortedChats.forEach(
        (chat) => {
          const personKey =
            getPersonKey(
              chat,
              userId
            );

          const user =
            getOtherUserFromChat(
              chat,
              userId
            );

          const existing =
            groups.get(
              personKey
            );

          if (!existing) {
            groups.set(
              personKey,
              {
                personKey,

                user,

                conversations:
                  [chat],

                totalUnread:
                  unreadForChat(
                    chat
                  ),

                latestChat:
                  chat,
              }
            );

            return;
          }

          existing.conversations.push(
            chat
          );

          existing.totalUnread +=
            unreadForChat(
              chat
            );
        }
      );

      return Array.from(
        groups.values()
      );
    }, [
      sortedChats,
      unreadForChat,
      userId,
    ]);

  /* ======================================================= */
  /* SEARCH */
  /* ======================================================= */

  const filteredGroups =
    useMemo(() => {
      const query =
        searchQuery
          .trim()
          .toLowerCase();

      if (!query) {
        return conversationGroups;
      }

      return conversationGroups
        .map(
          (group) => {
            const personMatches =
              group.user.username
                ?.toLowerCase()
                .includes(
                  query
                );

            const matchingConversations =
              personMatches
                ? group.conversations
                : group.conversations.filter(
                    (chat) => {
                      const itemName =
                        chat.itemName ||
                        `Item ${
                          chat.itemId ||
                          ""
                        }`;

                      const message =
                        chat.message ||
                        "";

                      return `${itemName} ${message}`
                        .toLowerCase()
                        .includes(
                          query
                        );
                    }
                  );

            if (
              !matchingConversations.length
            ) {
              return null;
            }

            return {
              ...group,

              conversations:
                matchingConversations,

              latestChat:
                matchingConversations[
                  0
                ],
            };
          }
        )
        .filter(Boolean);
    }, [
      conversationGroups,
      searchQuery,
    ]);

  /* ======================================================= */
  /* EXPAND PERSON */
  /* ======================================================= */

  const togglePersonGroup =
    useCallback(
      (personKey) => {
        setExpandedPeople(
          (current) => {
            const next =
              new Set(
                current
              );

            if (
              next.has(
                personKey
              )
            ) {
              next.delete(
                personKey
              );
            } else {
              next.add(
                personKey
              );
            }

            return next;
          }
        );
      },
      []
    );

  /* ======================================================= */
  /* TOTAL UNREAD */
  /* ======================================================= */

  const totalUnread =
    useMemo(
      () =>
        chats.reduce(
          (
            total,
            chat
          ) =>
            total +
            unreadForChat(
              chat
            ),
          0
        ),
      [
        chats,
        unreadForChat,
      ]
    );

  /* ======================================================= */
  /* TIME FORMAT */
  /* ======================================================= */

  const formatTime =
    (timestamp) => {
      const normalized =
        getChatTimestamp(
          timestamp
        );

      if (!normalized) {
        return "";
      }

      const diff =
        Date.now() -
        normalized;

      const minutes =
        Math.floor(
          diff / 60000
        );

      const hours =
        Math.floor(
          diff / 3600000
        );

      if (
        minutes < 1
      ) {
        return "now";
      }

      if (
        minutes < 60
      ) {
        return `${minutes}m`;
      }

      if (
        hours < 24
      ) {
        return `${hours}h`;
      }

      return new Date(
        normalized
      ).toLocaleDateString();
    };

  /* ======================================================= */
  /* RETURN */
  /* ======================================================= */

  return (
    <div className="flex h-[100dvh] min-h-0 flex-col overflow-hidden bg-background">
      {/* =================================================== */}
      {/* TINY SIDEBAR SCROLLBAR */}
      {/* =================================================== */}

      <style>{`
        .inbox-tiny-scrollbar {
          scrollbar-width: thin;
          scrollbar-color: rgba(148, 163, 184, 0.14) transparent;
        }

        .inbox-tiny-scrollbar::-webkit-scrollbar {
          width: 2px;
          height: 2px;
        }

        .inbox-tiny-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }

        .inbox-tiny-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(148, 163, 184, 0.12);
          border-radius: 999px;
        }

        .inbox-tiny-scrollbar:hover::-webkit-scrollbar-thumb {
          background: rgba(148, 163, 184, 0.22);
        }
      `}</style>

      <Navbar />

      {/* =================================================== */}
      {/* INBOX BODY */}
      {/* =================================================== */}

      <div className="flex min-h-0 flex-1 overflow-hidden">
        {/* ================================================= */}
        {/* SIDEBAR */}
        {/* ================================================= */}

        <aside
          className={`
            inbox-tiny-scrollbar
            w-full
            md:w-[330px]
            lg:w-[350px]
            min-h-0
            shrink-0
            overflow-y-auto
            overflow-x-hidden
            bg-card
            md:border-r
            md:border-border

            ${
              selectedChat
                ? "hidden md:block"
                : "block"
            }
          `}
        >
          {/* =============================================== */}
          {/* STICKY SIDEBAR HEADER */}
          {/* =============================================== */}

          <div
            className="
              sticky
              top-0
              z-20
              border-b
              border-border
              bg-card/95
              px-3
              py-3
              backdrop-blur-md
              sm:px-4
            "
          >
            {/* ============================================= */}
            {/* INBOX TITLE + BACK */}
            {/* ============================================= */}

            <div className="flex items-center justify-between gap-3">
              <div className="flex min-w-0 items-center gap-1.5">
                <button
                  type="button"
                  onClick={() =>
                    navigate(-1)
                  }
                  aria-label="Go back"
                  className="
                    -ml-1
                    flex
                    h-9
                    w-9
                    shrink-0
                    items-center
                    justify-center
                    rounded-full
                    text-foreground
                    transition-colors

                    hover:bg-muted
                    active:bg-muted/80
                  "
                >
                  <ArrowLeft
                    className="h-5 w-5"
                    strokeWidth={2.2}
                  />
                </button>

                <h1 className="truncate text-base font-bold">
                  Inbox
                </h1>
              </div>

              {totalUnread >
                0 && (
                <span
                  className="
                    shrink-0
                    rounded-full
                    bg-primary
                    px-2
                    py-0.5
                    text-[10px]
                    font-semibold
                    text-primary-foreground
                  "
                >
                  {totalUnread >
                  99
                    ? "99+"
                    : totalUnread}{" "}
                  unread
                </span>
              )}
            </div>

            {/* ============================================= */}
            {/* SEARCH */}
            {/* ============================================= */}

            <label className="relative mt-3 block">
              <Search
                className="
                  absolute
                  left-3
                  top-1/2
                  h-4
                  w-4
                  -translate-y-1/2
                  text-muted-foreground
                "
              />

              <input
                type="search"
                value={
                  searchQuery
                }
                onChange={(
                  event
                ) =>
                  setSearchQuery(
                    event.target
                      .value
                  )
                }
                placeholder="Search conversations..."
                className="
                  w-full
                  rounded-full
                  border
                  border-transparent
                  bg-secondary
                  py-2.5
                  pl-9
                  pr-4
                  text-sm
                  text-foreground
                  outline-none
                  transition

                  placeholder:text-muted-foreground

                  focus:border-primary/20
                  focus:bg-background
                  focus:ring-1
                  focus:ring-primary/30
                "
              />
            </label>
          </div>

          {/* =============================================== */}
          {/* CONVERSATIONS */}
          {/* =============================================== */}

          <div>
            {chats.length ===
              0 && (
              <div className="px-4 py-8 text-center">
                <p className="text-sm text-muted-foreground">
                  No conversations
                  yet
                </p>
              </div>
            )}

            {chats.length >
              0 &&
              filteredGroups.length ===
                0 && (
                <div className="px-4 py-8 text-center">
                  <p className="text-sm text-muted-foreground">
                    No conversations
                    match your
                    search.
                  </p>
                </div>
              )}

            {filteredGroups.map(
              (group) => {
                const {
                  user,
                  conversations,
                  personKey,
                } = group;

                const multipleConversations =
                  conversations.length >
                  1;

                const expanded =
                  expandedPeople.has(
                    personKey
                  ) ||
                  conversations.some(
                    (chat) =>
                      chat.roomId ===
                      selectedChat?.roomId
                  );

                const latestChat =
                  group.latestChat;

                const latestSentByMe =
                  Number(
                    latestChat.senderId
                  ) === userId;

                return (
                  <div
                    key={
                      personKey
                    }
                    className="
                      border-b
                      border-border/70
                    "
                  >
                    {/* ======================================= */}
                    {/* PERSON ROW */}
                    {/* ======================================= */}

                    <button
                      type="button"
                      onClick={() => {
                        if (
                          multipleConversations
                        ) {
                          togglePersonGroup(
                            personKey
                          );
                        } else {
                          handleSelectChat(
                            conversations[
                              0
                            ]
                          );
                        }
                      }}
                      className="
                        flex
                        w-full
                        items-center
                        gap-3
                        px-3
                        py-3
                        text-left
                        transition-colors

                        hover:bg-secondary/70
                        sm:px-4
                      "
                    >
                      {/* PROFILE IMAGE */}
                      <div className="relative shrink-0">
                        {user.profileImageUrl ? (
                          <ProfileImageViewer
                            src={
                              user.profileImageUrl
                            }
                            alt={`${
                              user.username ||
                              "User"
                            } profile`}
                            title={
                              user.username ||
                              "Profile image"
                            }
                            triggerClassName="h-12 w-12 rounded-full"
                            imageClassName="h-12 w-12 rounded-full object-cover"
                          />
                        ) : (
                          <div
                            className="
                              flex
                              h-12
                              w-12
                              items-center
                              justify-center
                              rounded-full
                              bg-primary
                              font-semibold
                              text-primary-foreground
                            "
                          >
                            {user.username
                              ?.charAt(
                                0
                              )
                              ?.toUpperCase() ||
                              "U"}
                          </div>
                        )}

                        {/* UNREAD BADGE */}
                        {group.totalUnread >
                          0 && (
                          <span
                            className="
                              absolute
                              -right-1
                              -top-1
                              flex
                              min-h-5
                              min-w-5
                              items-center
                              justify-center
                              rounded-full
                              bg-primary
                              px-1
                              text-[10px]
                              font-bold
                              text-primary-foreground
                              ring-2
                              ring-card
                            "
                          >
                            {group.totalUnread >
                            99
                              ? "99+"
                              : group.totalUnread}
                          </span>
                        )}
                      </div>

                      {/* USER DETAILS */}
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-2">
                          <p
                            className={`truncate text-sm ${
                              group.totalUnread >
                              0
                                ? "font-bold"
                                : "font-semibold"
                            }`}
                          >
                            {user.username}
                          </p>

                          <span
                            className={`shrink-0 text-[11px] ${
                              group.totalUnread >
                              0
                                ? "font-medium text-primary"
                                : "text-muted-foreground"
                            }`}
                          >
                            {formatTime(
                              latestChat.timestamp
                            )}
                          </span>
                        </div>

                        {/* ITEM CONVERSATION COUNT */}
                        <div className="mt-0.5 flex items-center gap-1 text-[11px] text-muted-foreground">
                          <Package className="h-3.5 w-3.5 shrink-0" />

                          <span className="truncate">
                            {
                              conversations.length
                            }{" "}
                            item{" "}
                            {conversations.length ===
                            1
                              ? "conversation"
                              : "conversations"}
                          </span>
                        </div>

                        {/* LAST MESSAGE */}
                        <p
                          className={`mt-0.5 truncate text-xs ${
                            group.totalUnread >
                            0
                              ? "font-medium text-foreground"
                              : "text-muted-foreground"
                          }`}
                        >
                          {latestSentByMe
                            ? "You: "
                            : ""}

                          {latestChat.message ||
                            "Conversation"}
                        </p>
                      </div>

                      {/* EXPAND ICON */}
                      {multipleConversations &&
                        (expanded ? (
                          <ChevronDown
                            className="
                              h-4
                              w-4
                              shrink-0
                              text-muted-foreground
                            "
                          />
                        ) : (
                          <ChevronRight
                            className="
                              h-4
                              w-4
                              shrink-0
                              text-muted-foreground
                            "
                          />
                        ))}
                    </button>

                    {/* ======================================= */}
                    {/* ITEM CONVERSATIONS */}
                    {/* ======================================= */}

                    {multipleConversations &&
                      expanded && (
                        <div
                          className="
                            relative
                            bg-secondary/20
                            py-1
                            pl-8
                          "
                        >
                          {/* VERTICAL GROUP GUIDE */}
                          <div
                            className="
                              absolute
                              bottom-2
                              left-[28px]
                              top-2
                              w-px
                              bg-border
                            "
                          />

                          {conversations.map(
                            (chat) => {
                              const isActive =
                                selectedChat?.roomId ===
                                chat.roomId;

                              const sentByMe =
                                Number(
                                  chat.senderId
                                ) ===
                                userId;

                              const unreadCount =
                                unreadForChat(
                                  chat
                                );

                              const unread =
                                unreadCount >
                                0;

                              const draft =
                                sessionStorage.getItem(
                                  `chat_draft_${userId}_${chat.roomId}`
                                );

                              const itemLabel =
                                chat.itemName ||
                                `Item #${chat.itemId}`;

                              return (
                                <button
                                  type="button"
                                  key={
                                    chat.roomId
                                  }
                                  onClick={() =>
                                    handleSelectChat(
                                      chat
                                    )
                                  }
                                  className={`
                                    relative
                                    flex
                                    w-full
                                    items-center
                                    gap-3
                                    px-3
                                    py-2.5
                                    text-left
                                    transition-colors

                                    ${
                                      isActive
                                        ? "bg-primary/8"
                                        : unread
                                        ? "bg-primary/5 hover:bg-primary/8"
                                        : "hover:bg-background/60"
                                    }
                                  `}
                                >
                                  {/* ACTIVE INDICATOR */}
                                  {isActive && (
                                    <span
                                      className="
                                        absolute
                                        bottom-2
                                        left-0
                                        top-2
                                        w-[3px]
                                        rounded-full
                                        bg-primary
                                      "
                                    />
                                  )}

                                  {/* ITEM IMAGE */}
                                  <div
                                    className="
                                      flex
                                      h-10
                                      w-10
                                      shrink-0
                                      items-center
                                      justify-center
                                      overflow-hidden
                                      rounded-lg
                                      bg-muted
                                    "
                                  >
                                    {chat.itemImageUrl ? (
                                      <img
                                        src={
                                          chat.itemImageUrl
                                        }
                                        alt=""
                                        className="h-full w-full object-cover"
                                        loading="lazy"
                                      />
                                    ) : (
                                      <Package
                                        className="
                                          h-4
                                          w-4
                                          text-muted-foreground
                                        "
                                      />
                                    )}
                                  </div>

                                  {/* ITEM CHAT INFO */}
                                  <div className="min-w-0 flex-1">
                                    <div className="flex items-center justify-between gap-2">
                                      <p
                                        className={`truncate text-xs ${
                                          unread
                                            ? "font-bold"
                                            : "font-semibold"
                                        }`}
                                      >
                                        {
                                          itemLabel
                                        }
                                      </p>

                                      <span
                                        className={`shrink-0 text-[10px] ${
                                          unread
                                            ? "font-medium text-primary"
                                            : "text-muted-foreground"
                                        }`}
                                      >
                                        {formatTime(
                                          chat.timestamp
                                        )}
                                      </span>
                                    </div>

                                    {/* DRAFT / LAST MESSAGE */}
                                    <p
                                      className={`mt-0.5 truncate text-xs ${
                                        unread
                                          ? "font-medium text-foreground"
                                          : "text-muted-foreground"
                                      }`}
                                    >
                                      {draft ? (
                                        <>
                                          <span className="font-medium text-destructive">
                                            Draft:{" "}
                                          </span>

                                          {
                                            draft
                                          }
                                        </>
                                      ) : (
                                        <>
                                          {sentByMe
                                            ? "You: "
                                            : ""}

                                          {chat.message ||
                                            "Conversation"}
                                        </>
                                      )}
                                    </p>
                                  </div>

                                  {/* UNREAD COUNT */}
                                  {unread && (
                                    <span
                                      className="
                                        flex
                                        min-h-5
                                        min-w-5
                                        shrink-0
                                        items-center
                                        justify-center
                                        rounded-full
                                        bg-primary
                                        px-1
                                        text-[10px]
                                        font-bold
                                        text-primary-foreground
                                      "
                                    >
                                      {unreadCount >
                                      99
                                        ? "99+"
                                        : unreadCount}
                                    </span>
                                  )}
                                </button>
                              );
                            }
                          )}
                        </div>
                      )}
                  </div>
                );
              }
            )}
          </div>
        </aside>

        {/* ================================================= */}
        {/* CHAT PANEL */}
        {/* ================================================= */}

        <main
          className={`
            min-h-0
            flex-1
            overflow-hidden
            bg-card
            flex-col

            ${
              selectedChat
                ? "flex"
                : "hidden md:flex"
            }
          `}
        >
          {selectedChat ? (
            <div className="min-h-0 flex-1 overflow-hidden">
              <ChatWindow
                key={
                  selectedChat.roomId
                }
                userId={
                  userId
                }
                otherUserId={
                  selectedChat.otherUserId
                }
                otherUser={{
                  userId:
                    selectedChat.otherUserId,

                  username:
                    selectedChat.otherUsername ||
                    "Marketplace user",

                  profileImageUrl:
                    selectedChat.otherProfileImageUrl ||
                    null,
                }}
                itemId={
                  selectedChat.itemId
                }

                /* MOBILE CHAT HEADER BACK BUTTON */
                onBack={
                  handleBackToInbox
                }

                onRoomRead={(
                  activityTime
                ) =>
                  markRoomRead(
                    selectedChat.roomId,
                    activityTime,
                    false
                  )
                }
              />
            </div>
          ) : (
            <div
              className="
                hidden
                h-full
                w-full
                items-center
                justify-center
                md:flex
              "
            >
              <div className="text-center">
                <p className="text-sm font-medium text-foreground">
                  Select a
                  conversation
                </p>

                <p className="mt-1 text-xs text-muted-foreground">
                  Choose a chat
                  from your inbox
                  to start
                  messaging.
                </p>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default InboxPage;
